import { UploadsApi } from "@/api/uploads.api";
import { formatDateLong } from "@/utils/format";
import type { CartRef, ImageFile } from "@/utils/types/types";

// Cart-aware upload delete (2026-09-03).
//
// Twice on 09-02 a staff cleanup deleted files that staff-built payment-link
// carts still pointed at; the customer then hit the missing-file checkout
// guard (Sentry TPW-ASTRO-WEB-UI-42). The server now refuses such deletes
// with 409 UPLOAD_IN_CART unless `force` is sent. This helper owns the prompt
// so all three uploads pages behave identically.
//
// Returns true if the file was deleted, false if the user cancelled.

export function describeCarts(inCarts: CartRef[]): string {
  return inCarts
    .map(c => {
      const label = c.name ? `"${c.name}"` : "an unnamed cart";
      const when = c.createdAt
        ? ` (created ${formatDateLong(new Date(c.createdAt))})`
        : "";
      return `${label}${when}`;
    })
    .join(", ");
}

function confirmInCart(image: ImageFile, inCarts: CartRef[]): boolean {
  const carts = describeCarts(inCarts);
  return window.confirm(
    `"${image.name}" is in ${inCarts.length === 1 ? "a cart" : `${inCarts.length} carts`} that ${inCarts.length === 1 ? "has" : "have"} not been ordered yet: ${carts}.\n\n` +
      `Deleting it will block that cart at checkout ("source file missing"). ` +
      `Delete anyway?`
  );
}

export async function deleteUploadGuarded(image: ImageFile): Promise<boolean> {
  // Known reference from the list payload → ask first, then force.
  if (image.inCarts && image.inCarts.length > 0) {
    if (!confirmInCart(image, image.inCarts)) return false;
    await UploadsApi.deleteFileByUrl(image.url, { force: true });
    return true;
  }

  // No known reference → plain delete; the server double-checks and may
  // answer 409 if a cart picked the file up since the list loaded.
  try {
    await UploadsApi.deleteFileByUrl(image.url);
    return true;
  } catch (err: any) {
    const status = err?.response?.status;
    const data = err?.response?.data;
    if (status === 409 && data?.error === "UPLOAD_IN_CART") {
      const inCarts: CartRef[] = Array.isArray(data.inCarts)
        ? data.inCarts
        : [];
      if (!confirmInCart(image, inCarts)) return false;
      await UploadsApi.deleteFileByUrl(image.url, { force: true });
      return true;
    }
    throw err;
  }
}
